import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Attendance} Attendance
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetAttendanceActionOpts
 * @property {string} organization
 * @property {string} uid
 *
 * @callback GetAttendanceAction
 * @param {ActionContext} ctx
 * @param {GetAttendanceActionOpts} opts
 * @returns {Promise<Attendance>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAttendanceAction}
 */
export const getAttendance = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.organization) throw new Error(`${nms}: Organization filter is required`);
  if (!opts.uid) throw new Error(`${nms}: Uid filter is required`);

  // fetch from sdk
  const query = {
    organization: opts.organization,
    uid: opts.uid
  };
  const attendance = await sdk.clocks().findOne(query);
  ctx.commit('updateInAttendances', { upsert: true, items: [ attendance ] });

  return attendance;
};

/**
 * @typedef {Object} GetAttendancesActionOpts
 * @property {string} organization
 * @property {string} uid
 *
 * @callback GetAttendancesAction
 * @param {ActionContext} ctx
 * @param {GetAttendancesActionOpts} opts
 * @returns {Promise<Attendance[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAttendancesAction}
 */
export const getAttendances = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.organization) throw new Error(`${nms}: Organization filter is required`);

  // fetch from sdk
  const query = {
    organization: opts.organization
  };
  if (opts.uid) query.uid = opts.uid;
  const attendance = await sdk.clocks().find(query).then(r => r.items);
  ctx.commit('updateInAttendances', { upsert: true, items: attendance });

  return attendance;
};

/**
 * @typedef {Object} MemberClockInActionOpts
 * @property {string} organization
 * @property {string} uid
 * @property {string} [accessToken]
 *
 * @callback MemberClockInAction
 * @param {ActionContext} ctx
 * @param {MemberClockInActionOpts} opts
 * @returns {Promise<Attendance>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {MemberClockInAction}
 */
export const memberClockIn = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const { organization, uid, accessToken } = opts;
  if (!organization) throw new Error(`${nms}: Organization required`);
  if (!uid) throw new Error(`${nms}: Uid required`);
  const data = { organization, uid };
  const attendance = await sdk.clocks().create(data, { accessToken });
  ctx.commit('updateInAttendances', { upsert: true, items: [ attendance ] });

  return attendance;
};

/**
 * @typedef {Object} MemberClockOutActionOpts
 * @property {string} attendance attendance id
 * @property {string} [accessToken]
 *
 * @callback MemberClockOutAction
 * @param {ActionContext} ctx
 * @param {MemberClockOutActionOpts} opts
 * @returns {Promise<Attendance>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {MemberClockOutAction}
 */
export const memberClockOut = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const { attendance: id, accessToken } = opts;
  if (!id) throw new Error(`${nms}: Attendance ID is required`);
  const data = { end: true };
  const attendance = await sdk.clocks().update(id, data, { accessToken });
  ctx.commit('updateInAttendances', { upsert: true, items: [ attendance ] });

  return attendance;
};
